import React, { useState } from 'react';
import { useToast } from '../../../../context/ToastContext';
import { testConnection } from '../api';

export default function WordPressSettings({ connection, onUpdate }) {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [settings, setSettings] = useState({
    name: connection.name || '',
    siteUrl: connection.settings?.siteUrl || '',
    username: connection.credentials?.username || '',
    applicationPassword: connection.credentials?.applicationPassword || ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTestResults(null);

    try {
      // Test the connection
      const testResult = await testConnection(settings);
      
      if (!testResult.success) {
        throw new Error(testResult.error || 'Connection test failed');
      }

      setTestResults(testResult.data.testResults);

      // Create a complete connection object with minimal settings
      const updatedConnection = {
        ...connection,
        name: settings.name.trim(),
        type: 'wordpress',
        status: 'active',
        credentials: {
          username: settings.username,
          applicationPassword: settings.applicationPassword
        },
        settings: {
          siteUrl: settings.siteUrl
        },
        is_configured: true,
        setup_required: false,
        last_tested_at: new Date().toISOString()
      };

      // Update the connection
      console.log('Updating connection:', updatedConnection);
      await onUpdate(updatedConnection);
    } catch (error) {
      console.error('Error updating WordPress connection:', error);
      
      const errorConnection = {
        ...connection,
        name: settings.name.trim(),
        type: 'wordpress',
        status: 'error',
        credentials: {
          username: settings.username,
          applicationPassword: settings.applicationPassword
        },
        settings: {
          siteUrl: settings.siteUrl
        },
        is_configured: false,
        setup_required: true,
        last_tested_at: new Date().toISOString()
      };
      
      await onUpdate(errorConnection);
      addToast(error.message || 'Failed to update connection', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">
          Connection Name
        </label>
        <input
          type="text"
          value={settings.name}
          onChange={(e) => setSettings({ ...settings, name: e.target.value })}
          placeholder="My WordPress Site"
          className="w-full px-3 py-2 bg-dark-300 border border-divider rounded focus:outline-none focus:border-accent-primary"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          WordPress Site URL
        </label>
        <input
          type="url"
          value={settings.siteUrl}
          onChange={(e) => setSettings({ ...settings, siteUrl: e.target.value })}
          placeholder="https://your-wordpress-site.com"
          className="w-full px-3 py-2 bg-dark-300 border border-divider rounded focus:outline-none focus:border-accent-primary"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          WordPress Username
        </label>
        <input
          type="text"
          value={settings.username}
          onChange={(e) => setSettings({ ...settings, username: e.target.value })}
          className="w-full px-3 py-2 bg-dark-300 border border-divider rounded focus:outline-none focus:border-accent-primary"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          Application Password
        </label>
        <input
          type="password"
          value={settings.applicationPassword}
          onChange={(e) => setSettings({ ...settings, applicationPassword: e.target.value })}
          placeholder="xxxx xxxx xxxx xxxx"
          className="w-full px-3 py-2 bg-dark-300 border border-divider rounded focus:outline-none focus:border-accent-primary"
          required
        />
        <p className="mt-1 text-sm text-gray-400">
          Generate this in your WordPress dashboard under Users → Profile → Application Passwords
        </p>
      </div>

      {testResults && (
        <div className="mt-4 p-4 bg-dark-200 rounded-lg">
          <h3 className="text-sm font-medium mb-2">Connection Test Results:</h3>
          <ul className="space-y-2">
            <li className="flex items-center">
              <span className={`mr-2 ${testResults.createPost ? 'text-green-500' : 'text-red-500'}`}>
                {testResults.createPost ? '✓' : '✗'}
              </span>
              Create Post Test
            </li>
            <li className="flex items-center">
              <span className={`mr-2 ${testResults.deletePost ? 'text-green-500' : 'text-red-500'}`}>
                {testResults.deletePost ? '✓' : '✗'}
              </span>
              Delete Post Test
            </li>
          </ul>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 disabled:opacity-50"
        >
          {loading ? 'Testing Connection...' : 'Save Connection'}
        </button>
      </div>
    </form>
  );
} 